// src/pages/solicitudes/SolicitudesActivas.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Alert from 'react-bootstrap/Alert'
import { toast } from 'react-toastify'
import Paginacion from 'components/Paginacion'
import api from 'api/api'

import './solicitudes.css'
import { DEFAULT_FETCH_CUANTOS } from 'utils/constants'
import { debounce } from 'utils/debounce'
import { toDisplayDate, toDisplayDateTime } from 'utils/date'
import VerSolicitud from './VerSolicitud'
import FiltrosSolicitudes from './FiltrosSolicitudes'
import CancelarSolicitudModal from './CancelarSolicitudModal'

// Estados que corresponden a solicitudes activas
const ESTADOS_ACTIVAS = ['pendiente', 'confirmando_pago', 'pagado', 'aceptado']

function SolicitudesActivas() {
	const [fetchDeACuantos, setFetchDeACuantos] = useState(DEFAULT_FETCH_CUANTOS)
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [loading, setLoading] = useState(false)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [filtroCategoria, setFiltroCategoria] = useState('')
	const [filtroCiudad, setFiltroCiudad] = useState('')
	const [filtroTipo, setFiltroTipo] = useState('')
	const [filtroEstado, setFiltroEstado] = useState('')

	const [solicitudes, setSolicitudes] = useState([])
	const [solicitudActual, setSolicitudActual] = useState(null)
	const [mostrarDetalle, setMostrarDetalle] = useState(false)

	// Estado para el modal de cancelacion
	const [mostrarModalCancelar, setMostrarModalCancelar] = useState(false)
	const [solicitudACancelar, setSolicitudACancelar] = useState(null)

	const opcionesCuantosMostrar = [10, 25, 50]

	const refreshSolicitudes = async () => {
		try {
			setLoading(true)

			// Construir filtros
			const filtros = {
				estados: filtroEstado ? [filtroEstado] : ESTADOS_ACTIVAS
			}

			if (filtroBusqueda) filtros.busqueda = filtroBusqueda
			if (filtroCategoria) filtros.categoriaId = filtroCategoria
			if (filtroCiudad) filtros.ciudadId = filtroCiudad
			if (filtroTipo) filtros.tipo = filtroTipo

			const { datos, error } = await api.post('a/ver-solicitudes', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros
			})

			if (error) {
				toast.error('Error al cargar solicitudes: ' + error)
				return
			}

			setSolicitudes(datos.solicitudes || [])
			setNumResultados(datos.numResultados || 0)
		} catch (error) {
			toast.error('Error de conexion')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const handleBusqueda = (value) => {
		setFiltroBusqueda(value || '')
	}

	const debouncedBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	// Efecto para debounce de busqueda
	useEffect(() => {
		debouncedBusqueda(busqueda)
	}, [busqueda, debouncedBusqueda])

	// Ref para controlar si es el primer render
	const isFirstRender = React.useRef(true)

	// Ref para evitar doble llamada al cambiar filtros
	const skipNextPaginaEffect = React.useRef(false)

	// Efecto para carga inicial
	useEffect(() => {
		refreshSolicitudes()
	}, [])

	// Efecto cuando cambia la paginacion
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false
			return
		}

		if (skipNextPaginaEffect.current) {
			skipNextPaginaEffect.current = false
			return
		}

		refreshSolicitudes()
	}, [paginaAct])

	// Efecto cuando cambian los filtros (resetea a pagina 1 y recarga)
	useEffect(() => {
		if (isFirstRender.current) {
			return
		}

		if (paginaAct !== 1) {
			skipNextPaginaEffect.current = true
			setPaginaAct(1)
		}

		refreshSolicitudes()
	}, [filtroBusqueda, filtroCategoria, filtroCiudad, filtroTipo, filtroEstado, fetchDeACuantos])

	const handleVerSolicitud = (solicitud) => {
		setSolicitudActual(solicitud)
		setMostrarDetalle(true)
	}

	const cerrarDetalle = () => {
		setMostrarDetalle(false)
		setSolicitudActual(null)
	}

	// Handlers para cancelacion
	const handleCancelarClick = (solicitud) => {
		setSolicitudACancelar(solicitud)
		setMostrarModalCancelar(true)
	}

	const handleCerrarModalCancelar = () => {
		setMostrarModalCancelar(false)
		setSolicitudACancelar(null)
	}

	const handleSolicitudCancelada = (solicitudId) => {
		// Remover la solicitud de la lista local
		setSolicitudes(prev => prev.filter(s => s.id !== solicitudId))
		setNumResultados(prev => prev - 1)
	}

	const renderEstadoBadge = (estado) => {
		const config = {
			pendiente: { bg: 'secondary', text: 'Pendiente' },
			confirmando_pago: { bg: 'info', text: 'Confirmando pago' },
			pagado: { bg: 'primary', text: 'Pagado' },
			aceptado: { bg: 'success', text: 'Publicado' }
		}
		const { bg, text } = config[estado] || { bg: 'secondary', text: estado }
		return <Badge bg={bg}>{text}</Badge>
	}

	const renderTipoBadge = (tipo) => {
		const config = {
			urgencia: { className: 'badge-urgencia', text: 'Urgencia' },
			presupuesto: { className: 'badge-presupuesto', text: 'Presupuesto' },
			evaluacion: { className: 'badge-evaluacion', text: 'Evaluación' }
		}
		const { className, text } = config[tipo] || { className: 'badge', text: tipo }
		return <Badge bg={'none'} className={className}>{text}</Badge>
	}

	return (
		<Container fluid id="solicitudes" className="vh-100">
			<Row className="d-flex no-gutters">
				<Col xs={12} className="mt-2">
					<h1>Solicitudes Activas</h1>
					<hr />
				</Col>
			</Row>

			{/* Barra de busqueda y filtros */}
			<Row className="d-flex no-gutters">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center">
							<Col sm={6} md={2}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Group style={{ display: 'inline-block' }}>
										<Form.Select
											size="sm"
											onChange={(e) => setFetchDeACuantos(e.target.value)}
											className="mx-1"
										>
											{opcionesCuantosMostrar.map((o) => (
												<option value={o} key={`mostrar-${o}`}>{o}</option>
											))}
										</Form.Select>
									</Form.Group>
									<span className="ms-2">resultados</span>
								</small>
							</Col>
							<Col md={4} lg={3}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar por ID, descripcion, usuario..."
											aria-label="Buscar"
										/>
										<InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>

			{/* Filtros adicionales */}
			<FiltrosSolicitudes
				filtroCategoria={filtroCategoria}
				setFiltroCategoria={setFiltroCategoria}
				filtroCiudad={filtroCiudad}
				setFiltroCiudad={setFiltroCiudad}
				filtroTipo={filtroTipo}
				setFiltroTipo={setFiltroTipo}
				filtroEstado={filtroEstado}
				setFiltroEstado={setFiltroEstado}
				estadosDisponibles={ESTADOS_ACTIVAS}
			/>

			{/* Lista de solicitudes */}
			<Row>
				<Col sm={12}>
					{loading ? (
						<div className="text-center my-5">
							<Spinner
								animation="border"
								size="lg"
								role="status"
								aria-hidden="true"
								variant="primary"
							/>
							<p className="mt-2">Cargando solicitudes...</p>
						</div>
					) : solicitudes.length === 0 ? (
						<Alert variant="info" className="my-4">
							No se encontraron solicitudes activas
						</Alert>
					) : (
						<Table id="table-solicitudes" className="table table-striped table-hover">
							<thead>
								<tr>
									<th>ID</th>
									<th>Tipo</th>
									<th>Categoría</th>
									<th>Usuario</th>
									<th>Ciudad</th>
									<th>Estado</th>
									<th>Fecha</th>
									<th>Fixers</th>
									<th>Propuestas</th>
									<th>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{solicitudes.map((solicitud) => {

									return (
										<tr key={solicitud.id}>
											<td><strong>#{solicitud.id}</strong></td>
											<td>{renderTipoBadge(solicitud.tipo)}</td>
											<td>
												<div className="d-flex align-items-center">
													{solicitud.categoria.imagen2Url && (
														<img
															src={process.env.REACT_APP_API_URL + 'uploads/' + solicitud.categoria.imagen2Url}
															alt=""
															className="categoria-icon me-2"
														/>
													)}
													<span>{solicitud.categoria.nombre}</span>
												</div>
											</td>
											<td>
												<div>
													<span>{solicitud.usuario.nombre}</span>
													{solicitud.usuario.telefono && (
														<small className="d-block text-muted">{solicitud.usuario.telefono}</small>
													)}
												</div>
											</td>
											<td>{solicitud.ciudad || '-'}</td>
											<td>{renderEstadoBadge(solicitud.estado)}</td>
											<td>{toDisplayDateTime(solicitud.fechaCreacion)}</td>
											<td>
												<Badge bg={solicitud.cantidadInteracciones > 0 ? 'info' : 'secondary'}>
													{solicitud.cantidadInteracciones}
												</Badge>
											</td>
											<td>
												<Badge bg={solicitud.cantidadPropuestas > 0 ? 'success' : 'secondary'}>
													{solicitud.cantidadPropuestas}
												</Badge>
											</td>
											<td>
												<div className="d-flex gap-1">
													<Button
														variant="outline-primary"
														size="sm"
														onClick={() => handleVerSolicitud(solicitud)}
														title="Ver detalle"
													>
														<i className="fa fa-eye"></i>
													</Button>
													<Button
														variant="outline-danger"
														size="sm"
														onClick={() => handleCancelarClick(solicitud)}
														title="Cancelar solicitud"
													>
														<i className="fa fa-times"></i>
													</Button>
												</div>
											</td>
										</tr>
									)
								})}
							</tbody>
						</Table>
					)}
					<Col>
						<Paginacion
							numResultados={numResultados}
							pagina={paginaAct}
							setPagina={setPaginaAct}
							deACuantos={fetchDeACuantos}
						/>
					</Col>
				</Col>
			</Row>

			{/* Panel de detalle */}
			{mostrarDetalle && solicitudActual && (
				<VerSolicitud
					solicitudId={solicitudActual.id}
					onClose={cerrarDetalle}
				/>
			)}

			{/* Modal de cancelacion */}
			<CancelarSolicitudModal
				show={mostrarModalCancelar}
				onHide={handleCerrarModalCancelar}
				solicitud={solicitudACancelar}
				onCancelado={handleSolicitudCancelada}
			/>
		</Container>
	)
}

export default SolicitudesActivas